// Zero-Cost Local Storage & Order Service for Guru's Workshop LLC
// Provides persistent storage for RFPs, Client Project Lifecycles, and Staged Milestone Proposals.
import { syncProjectToCloud } from './cloudSyncService';

const STORAGE_KEYS = {
  RFPS: 'gurus_workshop_rfps',
  ORDERS: 'gurus_workshop_orders',
  CART: 'gurus_workshop_cart',
  PROJECTS: 'gurus_workshop_projects'
};

export const getStoredProjects = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PROJECTS);
    if (!data) {
      localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify([]));
      return [];
    }
    return JSON.parse(data);
  } catch (err) {
    console.error('Error reading projects', err);
    return [];
  }
};

export const getProjectById = (id) => {
  if (!id) return null;
  const projects = getStoredProjects();
  return projects.find(p => p.id.toUpperCase() === id.trim().toUpperCase()) || null;
};

export const saveProjectRequest = (requestData) => {
  try {
    const existing = getStoredProjects();
    const id = `GW-REQ-${Math.floor(1000 + Math.random() * 9000)}`;
    const newProject = {
      id,
      createdAt: new Date().toISOString(),
      status: 'Under Review',
      category: requestData.category || 'Custom Software Build',
      platforms: requestData.platforms && requestData.platforms.length > 0 ? requestData.platforms : [requestData.category || 'Web Application'],
      title: requestData.title || `${requestData.category || 'Custom'} Project`,
      description: requestData.description || requestData.details || '',
      timelineTarget: requestData.timelineTarget || 'Flexible / Need Advice',
      client: {
        name: requestData.name || 'Client',
        email: requestData.email || '',
        phone: requestData.phone || '',
        organization: requestData.organization || ''
      },
      proposal: null,
      signature: null
    };

    const updated = [newProject, ...existing];
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(updated));
    window.dispatchEvent(new Event('projects_updated'));
    try { syncProjectToCloud(newProject); } catch (e) {}
    return newProject;
  } catch (err) {
    console.error('Error saving project request', err);
    return null;
  }
};

export const updateProjectProposal = (id, proposalData) => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) return null;

    projects[index].proposal = {
      publishedAt: new Date().toISOString(),
      ...proposalData
    };
    projects[index].status = 'Proposal Ready';

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    try { syncProjectToCloud(projects[index]); } catch (e) {}
    return projects[index];
  } catch (err) {
    console.error('Error updating project proposal', err);
    return null;
  }
};

export const signOffProject = (id, signatureData) => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) return null;

    projects[index].signature = {
      legalName: signatureData.legalName,
      signedAt: new Date().toISOString(),
      hash: `SIG-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`
    };
    projects[index].status = 'Signed & In Production';

    // Unlock Milestone 1 for payment if not already paid
    if (projects[index].proposal && projects[index].proposal.milestones && projects[index].proposal.milestones.length > 0) {
      if (projects[index].proposal.milestones[0].status !== 'Paid') {
        projects[index].proposal.milestones[0].status = 'Pending Payment';
      }
    }

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    try { syncProjectToCloud(projects[index]); } catch (e) {}
    return projects[index];
  } catch (err) {
    console.error('Error signing off project', err);
    return null;
  }
};

export const payProjectMilestone = (id, milestoneIndex, paymentInfo = {}) => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) return null;

    const project = projects[index];
    if (!project.proposal || !project.proposal.milestones[milestoneIndex]) return null;

    project.proposal.milestones[milestoneIndex].status = 'Paid';
    project.proposal.milestones[milestoneIndex].paidAt = new Date().toISOString();
    project.proposal.milestones[milestoneIndex].paymentReference = paymentInfo.ref || `PAY-${Math.floor(100000 + Math.random() * 900000)}`;

    // Unlock next milestone if present
    if (project.proposal.milestones[milestoneIndex + 1]) {
      project.proposal.milestones[milestoneIndex + 1].status = 'Pending Payment';
    } else {
      // All milestones completed
      project.status = 'Completed';
    }

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    try { syncProjectToCloud(project); } catch (e) {}
    return project;
  } catch (err) {
    console.error('Error paying milestone', err);
    return null;
  }
};

// Lifecycle Management: Delete / Archive Project
export const deleteProject = (id) => {
  try {
    const projects = getStoredProjects();
    const updated = projects.filter(p => p.id.toUpperCase() !== id.trim().toUpperCase());
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(updated));
    window.dispatchEvent(new Event('projects_updated'));
    return true;
  } catch (err) {
    console.error('Error deleting project', err);
    return false;
  }
};

// Status Direct Update
export const updateProjectStatus = (id, newStatus) => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id.toUpperCase() === id.trim().toUpperCase());
    if (index === -1) return null;

    projects[index].status = newStatus;
    projects[index].updatedAt = new Date().toISOString();

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    return projects[index];
  } catch (err) {
    console.error('Error updating project status', err);
    return null;
  }
};

// Request More Info Flow (Admin -> Client)
export const requestProjectMoreInfo = (id, questions, adminNotes = '') => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id.toUpperCase() === id.trim().toUpperCase());
    if (index === -1) return null;

    const formattedQuestions = Array.isArray(questions) ? questions : [questions];
    const newInquiry = {
      id: `INQ-${Date.now()}`,
      askedAt: new Date().toISOString(),
      adminNotes: adminNotes.trim(),
      questions: formattedQuestions.filter(q => q && q.trim()),
      clientReply: null,
      status: 'pending_client_reply'
    };

    const project = projects[index];
    project.status = 'Needs More Info';
    project.updatedAt = new Date().toISOString();
    project.inquiryThread = [...(project.inquiryThread || []), newInquiry];
    project.clarification = {
      questions: newInquiry.questions,
      adminNotes: newInquiry.adminNotes,
      requestedAt: newInquiry.askedAt,
      clientResponse: null
    };

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    return project;
  } catch (err) {
    console.error('Error requesting more info', err);
    return null;
  }
};

// Client Clarification Reply Flow (Client -> Admin)
export const submitProjectClarification = (id, replyText) => {
  try {
    const projects = getStoredProjects();
    const index = projects.findIndex(p => p.id.toUpperCase() === id.trim().toUpperCase());
    if (index === -1) return null;

    const project = projects[index];
    const thread = project.inquiryThread || [];
    
    // Attach reply to latest pending inquiry or create new reply entry
    if (thread.length > 0 && thread[thread.length - 1].status === 'pending_client_reply') {
      thread[thread.length - 1].clientReply = replyText.trim();
      thread[thread.length - 1].repliedAt = new Date().toISOString();
      thread[thread.length - 1].status = 'answered';
    } else {
      thread.push({
        id: `REPLY-${Date.now()}`,
        clientReply: replyText.trim(),
        repliedAt: new Date().toISOString(),
        status: 'answered'
      });
    }

    project.inquiryThread = thread;
    project.clarification = {
      ...(project.clarification || {}),
      clientResponse: replyText.trim(),
      repliedAt: new Date().toISOString()
    };
    project.status = 'Under Review'; // Automatically transitions back to review!
    project.updatedAt = new Date().toISOString();

    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    window.dispatchEvent(new Event('projects_updated'));
    return project;
  } catch (err) {
    console.error('Error submitting project clarification', err);
    return null;
  }
};

// Compatibility for RFPs, Client Orders, and Service Cart
export const getStoredRFPs = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.RFPS);
    return data ? JSON.parse(data) : [];
  } catch (err) {
    return [];
  }
};

export const saveRFP = (rfpData) => {
  try {
    const existing = getStoredRFPs();
    const id = `RFP-2026-${Math.floor(1000 + Math.random() * 9000)}`;
    const newRFP = {
      id,
      submittedAt: new Date().toISOString(),
      status: 'New',
      ...rfpData
    };
    const updated = [newRFP, ...existing];
    localStorage.setItem(STORAGE_KEYS.RFPS, JSON.stringify(updated));

    // Also mirror into universal projects so client gets instant tracking!
    saveProjectRequest({
      category: rfpData.projectType || 'Software RFP',
      title: `${rfpData.projectType || 'Software'} Architecture Build`,
      description: rfpData.projectScope || rfpData.keyDeliverables || 'Comprehensive software architecture specification.',
      timelineTarget: rfpData.timeline || '1 - 3 Months',
      name: rfpData.contactName || rfpData.companyName,
      email: rfpData.email,
      phone: rfpData.phone,
      organization: rfpData.companyName
    });

    return newRFP;
  } catch (err) {
    console.error('Error saving RFP to storage', err);
    return null;
  }
};

export const getStoredOrders = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.ORDERS);
    return data ? JSON.parse(data) : [];
  } catch (err) {
    return [];
  }
};

export const saveOrder = (orderData) => {
  try {
    const existing = getStoredOrders();
    const id = `GW-ORD-${Math.floor(10000 + Math.random() * 90000)}`;
    const newOrder = {
      id,
      orderDate: new Date().toISOString(),
      status: 'Pending Fulfillment',
      ...orderData
    };
    const updated = [newOrder, ...existing];
    localStorage.setItem(STORAGE_KEYS.ORDERS, JSON.stringify(updated));

    // Also mirror into universal projects
    saveProjectRequest({
      category: 'Software Engineering Services',
      title: orderData.items && orderData.items.length > 0 ? orderData.items.map(i => i.title).join(', ') : 'Client Service Order',
      description: orderData.customer?.notes || 'Client deliverable scope and order details.',
      timelineTarget: '2 - 3 Weeks',
      name: orderData.customer?.name,
      email: orderData.customer?.email,
      phone: orderData.customer?.phone,
      organization: orderData.customer?.address
    });

    return newOrder;
  } catch (err) {
    console.error('Error saving order to storage', err);
    return null;
  }
};

export const getCart = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CART);
    return data ? JSON.parse(data) : [];
  } catch (err) {
    return [];
  }
};

export const saveCart = (cartItems) => {
  try {
    localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(cartItems));
    window.dispatchEvent(new Event('cart_updated'));
  } catch (err) {
    console.error('Error updating cart', err);
  }
};

export const addToCart = (product) => {
  const current = getCart();
  const existingIndex = current.findIndex(item => item.id === product.id && item.customKey === product.customKey);
  
  let updated;
  if (existingIndex > -1) {
    updated = [...current];
    updated[existingIndex].quantity = (updated[existingIndex].quantity || 1) + (product.quantity || 1);
  } else {
    updated = [...current, { ...product, quantity: product.quantity || 1 }];
  }
  
  saveCart(updated);
  return updated;
};

export const clearCart = () => {
  saveCart([]);
};
